import { useContext } from 'react';
import { Button } from '@qodo/design-system';
import { AuthContext } from 'app/auth';
import { CopyButton } from '../../components/CopyButton';
import { LogoOnlyHeader } from '../../components/LogoOnlyHeader';
import { CONTACT_SUPPORT_URL } from '../../consts/url';
import { EmptyState } from '../../icons/EmptyState';
import { QUERY_PARAMS, ROUTES } from '../../routes/consts';
import { mixpanelService } from '../../services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from '../../services/mixpanel/consts';
import { isAirgappedMode } from '../../utils/env';
import { spawn } from '../../utils/translations';
import {
  getQueryParam,
  openUrl,
  goToPathname,
  getTraceIdFromSessionStorage,
  getSessionIdFromSessionStorage,
} from '../../utils/url';
import { ERROR_TYPES, ICON_HEIGHT } from './consts';
import { ErrorProps } from './types';

export const Error = ({ type = ERROR_TYPES.FALLBACK }: ErrorProps) => {
  // IMPORTANT:
  // This page is also used as an ErrorBoundary fallback.
  // In that state, React Query / Auth providers may not be mounted.
  // We only rely on AuthContext (safe: returns default value if provider is missing).
  const { user: authUser } = useContext(AuthContext);
  const isAuthenticated = !!authUser;

  const errorType = getQueryParam(QUERY_PARAMS.INTERNAL_STATUS_CODE) || type;
  const traceId = getTraceIdFromSessionStorage();
  const sessionId = getSessionIdFromSessionStorage();

  // We intentionally show a generic error message (no specifics), while keeping
  // navigation + metadata for support/debug.
  const spawnError = spawn(`error.${errorType}`);
  const spawnGeneralError = spawn('error');
  const spawnMetadata = spawn('error.metadata');

  const getResetSearchParams = () => {
    const currentSearchParams = new URLSearchParams(window.location.search);
    const resetSearchParams = new URLSearchParams();

    // Remove all search params for fresh start, except auth_v2
    if (currentSearchParams.get(QUERY_PARAMS.AUTH_V2)) {
      resetSearchParams.set(QUERY_PARAMS.AUTH_V2, 'true');
    }

    return resetSearchParams;
  };

  const onContactSupportClick = () => {
    openUrl(CONTACT_SUPPORT_URL, true);
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.CONTACT_SUPPORT,
    });
  };

  const onGoToLoginPageClick = () => {
    const resetSearchParams = getResetSearchParams();

    goToPathname(ROUTES.SIGNIN, resetSearchParams);
  };

  const onGoToHomeClick = () => {
    const resetSearchParams = getResetSearchParams();

    goToPathname(ROUTES.ROOT, resetSearchParams);
  };

  const renderNavigationRecoveryButton = () => {
    const isPageNotFound =
      errorType === ERROR_TYPES.PAGE_NOT_FOUND || isAuthenticated;
    const onButtonClick = isPageNotFound
      ? onGoToHomeClick
      : onGoToLoginPageClick;

    return (
      <Button variant="secondary" onClick={onButtonClick}>
        {isPageNotFound
          ? spawnGeneralError('go-to-home')
          : spawnGeneralError('go-to-login')}
      </Button>
    );
  };

  const renderMetadataField = (labelKey: string, value: string) => {
    const label = spawnMetadata(labelKey);

    return (
      <div className="flex items-center gap-2 w-fit text-xs text-purple-muted-300">
        <span>{label}:</span>
        <span className="font-mono">{value}</span>
        <CopyButton
          value={value}
          title={spawnMetadata('copy-tooltip', { field: label.toLowerCase() })}
        />
      </div>
    );
  };

  const renderErrorMetadataIfNeeded = () => {
    if (!traceId && !sessionId) {
      return <></>;
    }

    return (
      <div className="mt-6 flex flex-col gap-2 max-w-150 w-full">
        <hr className="border-purple-muted-600" />
        <div className="pt-4 flex flex-col gap-2 items-center">
          <div className="text-xs text-purple-muted-300 mb-1">
            {spawnMetadata('title')}
          </div>
          {traceId && renderMetadataField('trace-id', traceId)}
          {sessionId && renderMetadataField('session-id', sessionId)}
        </div>
      </div>
    );
  };

  const renderContactSupportButtonIfNeeded = () => {
    if (errorType === ERROR_TYPES.PAGE_NOT_FOUND || isAirgappedMode()) {
      return <></>;
    }

    return (
      <Button variant="link" onClick={onContactSupportClick}>
        {spawnGeneralError('contact-support')}
      </Button>
    );
  };

  const renderSection = () => {
    return (
      <section className="h-screen flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-9 max-w-150 text-center">
          <EmptyState height={ICON_HEIGHT} />
          <div className="flex flex-col items-center justify-center gap-4 ">
            <h1 className="text-white">{spawnError('title')}</h1>
            <h4 className="text-purple-muted-100 font-normal">
              {spawnError('description')}
            </h4>
          </div>
          <div className="flex flex-col gap-4 items-center">
            {renderNavigationRecoveryButton()}
            {renderContactSupportButtonIfNeeded()}
          </div>
          {renderErrorMetadataIfNeeded()}
        </div>
      </section>
    );
  };

  return (
    <>
      <LogoOnlyHeader />
      {renderSection()}
    </>
  );
};
